import {Util} from './util'

interface TruncateOptions {
  /**
   * Maximum length after truncation
   */
  maxLength: number
  /**
   * Maximum length of a part after ellipsis
   */
  maxRight: number
  /**
   * Num chars of file name to use before extension
   */
  beforeExtension: number
}

const defaultTruncateOptions: TruncateOptions = {
  maxLength: 30,
  maxRight: 10,
  beforeExtension: 3
}

export class FilenameUtil {
  static dataUrl = ''
  // `<` and `>` are not in pattern to prevent HTML tags in message text from polluting file link
  static rx = /(\/root\/#file:\/user-data\/[-0-9a-f]+\/([^ \n<>]+))/g

  static removeFilenames(html: string): string {
    return html.replace(FilenameUtil.rx, () => ' ')
  }

  static getFilenames(html: string): string[] {
    return [...html.matchAll(this.rx)].map(x =>
      FilenameUtil.getFilename(x[2] ?? '')
    )
  }

  static absoluteUrlFromEncoded(encodedUrl: string): string {
    return FilenameUtil.dataUrl + this.relativeUrlFromEncoded(encodedUrl)
  }

  static relativeUrlFromEncoded(encodedUrl: string): string {
    const path = encodedUrl.split('/root/#file:').pop()?.split('/') ?? []
    const name = path.pop() ?? ''
    return path.join('/') + '/' + name
  }

  static getFilename(item: string): string {
    const link = FilenameUtil.absoluteUrlFromEncoded(
      Util.safeDecodeUriComponent(item, item)
    )
    return (
      Util.safeDecodeUriComponent(
        FilenameUtil.originalFileFromUrl(link).split('/').pop() ?? '',
        link.split('/')?.pop() ?? ''
      ) ||
      (FilenameUtil.originalFileFromUrl(link).split('/').pop() ?? '')
    )
  }

  static originalFileFromUrl(urlWithParameters: string): string {
    return urlWithParameters.includes('_____previews') // deprecated, old format
      ? urlWithParameters.split('_____previews').shift() ?? ''
      : urlWithParameters.split('?').shift() ?? ''
  }

  static truncateFileName(
    fileName: string,
    {
      maxLength,
      maxRight,
      beforeExtension
    }: TruncateOptions = defaultTruncateOptions
  ): string {
    const len = fileName.length
    if (len <= maxLength) {
      return fileName
    }

    const pos = fileName.lastIndexOf('.')
    if (len - pos < maxRight - beforeExtension) {
      // truncation includes full short extension; split some symbols before pos
      const splitAt = pos - beforeExtension
      const left = maxLength - (len - splitAt) - 1
      return fileName.slice(0, left) + '…' + fileName.slice(splitAt)
    } else {
      // truncation includes only a part of a large extension; split into halves
      const right = Math.floor((maxLength - 1) / 2)
      const left = maxLength - right - 1
      return fileName.slice(0, left) + '…' + fileName.slice(-right)
    }
  }
}
